import {
  getContextFromFeaturePrd,
  getFileContentsWithLineNumbers,
} from "@/components/AI/EngSpec/utils";
import { useSelectedElements } from "@/hooks/useSelectedElements";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useAiContextStore } from "@/store/zustand/aiContextStore";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore from "@/store/zustand/layoutStore";
import { useActiveCanvas, useCanvasId } from "@/utils/utils";
import { pick } from "lodash";
import { Storyboard } from "../types";
import { AIChatInput } from "./chatWithAI";
import { useIssuesStore } from "@/store/zustand/issues-store";
import { usePagesStore } from "@/store/zustand/pagesStore";
import { useEffect } from "react";
import { FeaturePRD } from "@/types/issues";
import { useState } from "react";

export const formatStoryboardForPrompt = (canvasId: string, sb: Storyboard) => {
  return {
    id: sb.id,
    name: sb.name,
    x: sb.x,
    y: sb.y,
    width: sb.width,
    height: sb.height,
    type: sb.type,

    // Make this the relative path (everything after canvas id)
    fullFilePath: sb.fullFilePath?.split(canvasId + "/")[1],
  };
};

export const useBaseInputsForPrompt = () => {
  const canvasId = useCanvasId();
  const featurePrd = useIssuesStore(
    (state) => state.canvasIdToIssue[canvasId]?.featurePrd,
  );

  // Get current page context
  const selectedPageId = usePagesStore(
    (state) => state.selectedPageId[canvasId],
  );
  const pages = usePagesStore((state) => state.pages[canvasId] || []);
  const currentPage = pages.find((p) => p.id === selectedPageId);
  // Get the page config path (relative to canvas root) for the selected page
  const pageConfigPath = currentPage?.configPath;

  const tokenSoftLimit = useAiActionStore((state) => state.tokenSoftLimit);
  const { layoutMode, leftTab } = useLayoutStore((state) =>
    pick(state, ["layoutMode", "leftTab"]),
  );
  const activeCanvas = useActiveCanvas();
  const fileToContents = useCodebaseStore((state) => state.fileToContents);
  const { selectedTreeElements, selectedStoryboards } = useSelectedElements();

  const { userSelectionContext } = getContextFromFeaturePrd(
    featurePrd ?? undefined,
  );

  // Combine selected elements and storyboards
  const totalUserSelection = [
    ...selectedTreeElements,
    ...selectedStoryboards.map((sb) => ({
      ...sb,
      isStoryboard: true,
    })),
  ];

  // Format storyboards in the canvas for the prompt
  const storyboardsInCanvas = canvasId
    ? activeCanvas?.storyboards
        ?.filter((sb: Storyboard) => !sb.deleted)
        ?.map((sb: Storyboard) => formatStoryboardForPrompt(canvasId, sb))
    : [];

  // Prepare code snippet related fields
  return {
    feature_title: featurePrd?.title || "",
    feature_prd: featurePrd?.prd || "",
    mermaid_diagram: featurePrd?.mermaid_diagram || "",
    user_selection_context: JSON.stringify(userSelectionContext),
    selected_elements: JSON.stringify(totalUserSelection),
    storyboards_in_canvas: JSON.stringify(storyboardsInCanvas || []),
    token_soft_limit: tokenSoftLimit * 1000,
    layout_mode: layoutMode,
    left_tab: leftTab,
    page_config_path: pageConfigPath || undefined,
    page_name: currentPage?.name || undefined,
  } satisfies Partial<AIChatInput>;
};
